import { useEffect } from 'react';
import { useAdminStore } from '../store/adminStore';
import { 
  TrendingUp, 
  ShoppingCart, 
  Package, 
  DollarSign,
  ArrowUp,
  ArrowDown,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
} from 'lucide-react';
import { ORDER_STATUS_LABELS } from '../types/admin';

export default function AdminDashboard() {
  const { 
    products, 
    orders, 
    getTotalRevenue, 
    getTotalOrders, 
    getTotalProducts,
    getRecentOrders,
    fetchOrdersFromAPI,
  } = useAdminStore();

  // Sayfa açılır açılmaz güncel veriyi çek; arka plan senkronizasyonu artık
  // AdminLayout tarafından panel genelinde yönetiliyor.
  useEffect(() => {
    fetchOrdersFromAPI();
  }, []);

  const totalRevenue = getTotalRevenue();
  const totalOrders = getTotalOrders();
  const totalProducts = getTotalProducts();
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const recentOrders = getRecentOrders(5);

  const stats = [
    {
      title: 'Toplam Gelir',
      value: `₺${totalRevenue.toLocaleString('tr-TR')}`,
      change: '+12.5%',
      isPositive: true,
      icon: DollarSign,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Toplam Sipariş',
      value: totalOrders.toString(),
      change: '+8',
      isPositive: true,
      icon: ShoppingCart,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Toplam Ürün',
      value: totalProducts.toString(),
      change: '+3',
      isPositive: true,
      icon: Package,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Bekleyen Sipariş',
      value: pendingOrders.toString(),
      change: pendingOrders > 0 ? 'Güncelle' : 'Temiz',
      isPositive: pendingOrders === 0,
      icon: Clock,
      color: pendingOrders > 0 ? 'bg-yellow-500' : 'bg-green-500',
      bgColor: pendingOrders > 0 ? 'bg-yellow-50' : 'bg-green-50',
    },
  ];

  const statusCounts = orders.reduce((acc, order) => {
    acc[order.status] = (acc[order.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const statusIcons: Record<string, React.ReactNode> = {
    pending: <Clock className="w-4 h-4 text-yellow-600" />,
    processing: <Package className="w-4 h-4 text-blue-600" />,
    shipped: <Truck className="w-4 h-4 text-purple-600" />,
    delivered: <CheckCircle className="w-4 h-4 text-green-600" />,
    cancelled: <XCircle className="w-4 h-4 text-red-600" />,
  };

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Hoş geldiniz! İşte mağazanızın güncel durumu.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div
            key={stat.title}
            className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                <div className="flex items-center gap-1 mt-2">
                  {stat.isPositive ? (
                    <ArrowUp className="w-4 h-4 text-green-500" />
                  ) : (
                    <ArrowDown className="w-4 h-4 text-red-500" />
                  )}
                  <span
                    className={`text-sm font-medium ${
                      stat.isPositive ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {stat.change}
                  </span>
                </div>
              </div>
              <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                <stat.icon className={`w-6 h-6 text-white ${stat.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Son Siparişler</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-6 py-3">Sipariş No</th>
                  <th className="px-6 py-3">Müşteri</th>
                  <th className="px-6 py-3">Tutar</th>
                  <th className="px-6 py-3">Durum</th>
                  <th className="px-6 py-3">Tarih</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm font-medium text-gray-900">
                        {order.orderNumber}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{order.customerName}</p>
                        <p className="text-xs text-gray-500">{order.city}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold text-gray-900">
                        ₺{order.totalAmount.toLocaleString('tr-TR')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {statusIcons[order.status]}
                        {ORDER_STATUS_LABELS[order.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-500">
                        {new Date(order.createdAt).toLocaleDateString('tr-TR')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Order Status Overview */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Sipariş Durumları</h2>
          <div className="space-y-4">
            {Object.entries(ORDER_STATUS_LABELS).map(([status, label]) => {
              const count = statusCounts[status] || 0;
              const percentage = totalOrders > 0 ? (count / totalOrders) * 100 : 0;
              return (
                <div key={status}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {statusIcons[status]}
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{count}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-brand-red transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick Stats */}
          <div className="mt-8 pt-6 border-t border-gray-100 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Ortalama Sipariş</span>
              <span className="text-sm font-semibold text-gray-900">
                ₺{totalOrders > 0 ? Math.round(totalRevenue / totalOrders).toLocaleString('tr-TR') : '0'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Ürün Çeşidi</span>
              <span className="text-sm font-semibold text-gray-900">{totalProducts}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
